// Modern SaaS / Glassmorphism page — monolithic iframe block
// 4 brand variants: pulse / aurora / ember / nova
// Shared between editor canvas renderer and published site renderer.

export type GlassmorphBrand = 'pulse' | 'aurora' | 'ember' | 'nova'
export interface GlassmorphPageData { brand: GlassmorphBrand }
export const DEFAULT_GLASSMORPH_DATA: GlassmorphPageData = { brand: 'pulse' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const GLASSMORPH_CSS = `
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{background:var(--bg);color:var(--ink);font-family:var(--fb);font-size:16px;line-height:1.6;overflow-x:hidden;transition:background-color .7s ease,color .7s ease}
::selection{background:var(--a1);color:#0b0b10}
h1,h2,h3,h4,.disp{font-family:var(--fd);letter-spacing:-.02em;line-height:1.12}
a{color:inherit;text-decoration:none}
button{font-family:inherit;cursor:pointer;border:none;background:none;color:inherit}
img{max-width:100%}
.container{max-width:1180px;margin:0 auto;padding:0 24px}
section{position:relative;padding:96px 0}
:root{--line:rgba(255,255,255,.12);--glass:rgba(255,255,255,.055);--glass-2:rgba(255,255,255,.09);--nav-h:76px}
[data-brand="pulse"]{--a1:#8b5cf6;--a2:#e14fd0;--a3:#22d3ee;--bg:#0a0812;--bg2:#120d20;--ink:#f5f3ff;--muted:#a7a0c8;--fd:'Space Grotesk Variable',sans-serif;--fb:'Inter Variable',sans-serif;--rad:18px;--rad-lg:26px;--rad-btn:14px}
[data-brand="aurora"]{--a1:#2dd4bf;--a2:#38bdf8;--a3:#a7f3d0;--bg:#03141a;--bg2:#062028;--ink:#eafffb;--muted:#8fb5b1;--fd:'Sora Variable',sans-serif;--fb:'Inter Variable',sans-serif;--rad:26px;--rad-lg:34px;--rad-btn:999px}
[data-brand="ember"]{--a1:#fb923c;--a2:#f43f5e;--a3:#fbbf24;--bg:#120905;--bg2:#1d0e08;--ink:#fff6ef;--muted:#c4a196;--fd:'Outfit Variable',sans-serif;--fb:'Inter Variable',sans-serif;--rad:12px;--rad-lg:18px;--rad-btn:10px}
[data-brand="nova"]{--a1:#f472b6;--a2:#c084fc;--a3:#a3e635;--bg:#0d0614;--bg2:#170a22;--ink:#fdf4ff;--muted:#b79cc4;--fd:'Unbounded Variable',sans-serif;--fb:'Inter Variable',sans-serif;--rad:30px;--rad-lg:40px;--rad-btn:999px}

.noise{position:fixed;inset:0;z-index:2;pointer-events:none;opacity:.05;mix-blend-mode:overlay;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}
.grid-bg{position:fixed;inset:0;z-index:0;pointer-events:none;opacity:.5;background:linear-gradient(var(--line) 1px,transparent 1px) 0 0/100% 72px,linear-gradient(90deg,var(--line) 1px,transparent 1px) 0 0/72px 100%;-webkit-mask-image:radial-gradient(70% 55% at 50% 0%,#000 0%,transparent 100%);mask-image:radial-gradient(70% 55% at 50% 0%,#000 0%,transparent 100%)}
.blob{position:fixed;border-radius:50%;filter:blur(90px);z-index:0;pointer-events:none;opacity:.5;transition:background .8s ease}
.blob.b1{width:560px;height:560px;top:-180px;left:-140px;background:radial-gradient(circle at 30% 30%,var(--a1),transparent 65%);animation:drift 22s ease-in-out infinite alternate}
.blob.b2{width:480px;height:480px;top:8%;right:-160px;background:radial-gradient(circle at 60% 40%,var(--a2),transparent 65%);animation:drift 26s ease-in-out infinite alternate-reverse}
.blob.b3{width:520px;height:520px;bottom:-220px;left:32%;background:radial-gradient(circle at 50% 50%,var(--a3),transparent 62%);opacity:.28;animation:drift 30s ease-in-out infinite alternate}
@keyframes drift{from{transform:translate(0,0) scale(1)}to{transform:translate(60px,40px) scale(1.12)}}

.glass{background:linear-gradient(155deg,var(--glass-2),var(--glass));border:1px solid var(--line);-webkit-backdrop-filter:blur(18px);backdrop-filter:blur(18px);border-radius:var(--rad-lg);box-shadow:0 20px 60px rgba(0,0,0,.35)}
.grad{background:linear-gradient(92deg,var(--a1),var(--a2) 60%,var(--a3));-webkit-background-clip:text;background-clip:text;color:transparent;transition:background .6s}

.btn{display:inline-flex;align-items:center;gap:10px;font-weight:600;font-size:15px;padding:15px 28px;border-radius:var(--rad-btn);transition:transform .25s,box-shadow .25s,filter .25s,background .4s;position:relative;overflow:hidden}
.btn:active{transform:scale(.97)}
.btn-primary{background:linear-gradient(92deg,var(--a1),var(--a2));color:#fff;box-shadow:0 10px 34px color-mix(in srgb,var(--a1) 40%,transparent)}
.btn-primary:hover{transform:translateY(-3px);filter:brightness(1.12)}
.btn-primary::after{content:"";position:absolute;top:0;left:-80%;width:50%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.35),transparent);transform:skewX(-20deg);transition:left .6s}
.btn-primary:hover::after{left:130%}
.btn-ghost{border:1px solid var(--line);background:var(--glass);-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px)}
.btn-ghost:hover{transform:translateY(-3px);border-color:color-mix(in srgb,var(--a1) 55%,transparent)}

header{position:fixed;top:0;left:0;right:0;z-index:50;transition:background .4s,border-color .4s;border-bottom:1px solid transparent}
header.scrolled{background:color-mix(in srgb,var(--bg) 72%,transparent);-webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);border-color:var(--line)}
.nav{height:var(--nav-h);display:flex;align-items:center;justify-content:space-between;gap:20px}
.logo{display:flex;align-items:center;gap:12px;font-family:var(--fd);font-weight:800;font-size:22px;letter-spacing:.02em}
.logo svg{width:40px;height:40px;flex:none}
.logo .edition{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--a1);border:1px solid color-mix(in srgb,var(--a1) 45%,transparent);padding:4px 9px;border-radius:999px;margin-left:2px}
.nav-links{display:flex;gap:30px;font-size:14.5px;font-weight:500;color:var(--muted)}
.nav-links a{position:relative;transition:color .3s}
.nav-links a::after{content:"";position:absolute;left:0;bottom:-6px;width:0;height:2px;border-radius:2px;background:linear-gradient(90deg,var(--a1),var(--a2));transition:width .3s}
.nav-links a:hover{color:var(--ink)}.nav-links a:hover::after{width:100%}
.nav-cta{display:flex;align-items:center;gap:14px}
.nav-cta .btn{padding:11px 22px;font-size:14px}
.burger{display:none;width:42px;height:42px;border-radius:12px;border:1px solid var(--line);background:var(--glass);position:relative;z-index:60}
.burger span{position:absolute;left:11px;right:11px;height:2px;border-radius:2px;background:var(--ink);transition:.35s}
.burger span:nth-child(1){top:15px}.burger span:nth-child(2){top:21px}.burger span:nth-child(3){top:27px}
body.menu-open .burger span:nth-child(1){top:21px;transform:rotate(45deg)}
body.menu-open .burger span:nth-child(2){opacity:0}
body.menu-open .burger span:nth-child(3){top:21px;transform:rotate(-45deg)}
.mnav{position:fixed;inset:0;z-index:55;display:flex;flex-direction:column;justify-content:center;gap:8px;padding:32px;background:color-mix(in srgb,var(--bg) 86%,transparent);-webkit-backdrop-filter:blur(24px);backdrop-filter:blur(24px);opacity:0;pointer-events:none;transition:opacity .4s}
body.menu-open .mnav{opacity:1;pointer-events:auto}
.mnav a{font-family:var(--fd);font-size:30px;font-weight:700;padding:10px 0;border-bottom:1px solid var(--line)}
.mnav a:hover{color:var(--a1)}

.hero{min-height:100svh;display:flex;align-items:center;padding:calc(var(--nav-h) + 56px) 0 90px;overflow:hidden}
.hero-word{position:absolute;top:6%;left:50%;transform:translateX(-50%);z-index:0;font-family:var(--fd);font-weight:800;font-size:clamp(90px,20vw,260px);letter-spacing:.04em;color:transparent;-webkit-text-stroke:1px color-mix(in srgb,var(--a1) 22%,transparent);opacity:.5;user-select:none;pointer-events:none;white-space:nowrap;transition:all .6s}
.hero-grid{display:grid;grid-template-columns:1.02fr .98fr;gap:56px;align-items:center;position:relative;z-index:1}
.badge{display:inline-flex;align-items:center;gap:9px;font-size:12.5px;font-weight:600;letter-spacing:.08em;padding:8px 16px;border-radius:999px;border:1px solid color-mix(in srgb,var(--a1) 40%,transparent);background:color-mix(in srgb,var(--a1) 12%,transparent);color:color-mix(in srgb,var(--a1) 70%,#fff);margin-bottom:26px}
.badge .pulse-dot{width:7px;height:7px;border-radius:50%;background:var(--a1);box-shadow:0 0 0 0 var(--a1);animation:ping 1.8s infinite}
@keyframes ping{70%{box-shadow:0 0 0 9px transparent}100%{box-shadow:0 0 0 0 transparent}}
.hero h1{font-size:clamp(2.3rem,4.9vw,3.9rem);font-weight:800;margin-bottom:22px}
[data-brand="nova"] .hero h1{font-size:clamp(1.7rem,3.9vw,3rem)}
.hero p.sub{color:var(--muted);font-size:17.5px;max-width:520px;margin-bottom:36px}
.hero-actions{display:flex;gap:16px;flex-wrap:wrap;margin-bottom:34px}
.hero-proof{display:flex;align-items:center;gap:14px;color:var(--muted);font-size:13.5px}
.avatars{display:flex}
.avatars i{width:34px;height:34px;border-radius:50%;border:2px solid var(--bg);margin-left:-9px;display:grid;place-items:center;font-style:normal;font-size:11px;font-weight:700;color:#0b0b10;background:linear-gradient(135deg,var(--a1),var(--a3))}
.avatars i:first-child{margin-left:0}
.stars{color:var(--a3);letter-spacing:2px;font-size:13px}

.hero-visual{position:relative;perspective:1200px}
.dash{padding:22px;border-radius:var(--rad-lg);transform-style:preserve-3d;transition:transform .3s ease;will-change:transform}
.dash-top{display:flex;align-items:center;gap:8px;margin-bottom:18px}
.dash-top .dot{width:11px;height:11px;border-radius:50%}
.dash-top .dot:nth-child(1){background:#ff5f57}.dash-top .dot:nth-child(2){background:#febc2e}.dash-top .dot:nth-child(3){background:#28c840}
.dash-top .url{margin-left:auto;font-size:11px;color:var(--muted);border:1px solid var(--line);border-radius:999px;padding:4px 14px}
.kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:14px}
.kpi{border:1px solid var(--line);background:color-mix(in srgb,var(--glass) 60%,transparent);border-radius:var(--rad);padding:14px 16px}
.kpi .lbl{font-size:11px;color:var(--muted);letter-spacing:.06em;text-transform:uppercase;margin-bottom:6px}
.kpi .val{font-family:var(--fd);font-weight:700;font-size:20px}
.kpi .delta{font-size:11.5px;font-weight:700;color:var(--a3)}
.chart{border:1px solid var(--line);border-radius:var(--rad);padding:16px 14px 8px;background:color-mix(in srgb,var(--glass) 55%,transparent)}
.chart svg{width:100%;height:auto;display:block}
.area-line{stroke:url(#lineG);stroke-width:3;fill:none;stroke-linecap:round;stroke-dasharray:1400;stroke-dashoffset:1400;animation:draw 2.4s .4s cubic-bezier(.6,0,.2,1) forwards}
@keyframes draw{to{stroke-dashoffset:0}}
.bars rect{fill:url(#barG);transform-origin:bottom;transform:scaleY(0);animation:grow .9s cubic-bezier(.2,.8,.2,1) forwards}
.bars rect:nth-child(1){animation-delay:.5s}.bars rect:nth-child(2){animation-delay:.62s}
.bars rect:nth-child(3){animation-delay:.74s}.bars rect:nth-child(4){animation-delay:.86s}
.bars rect:nth-child(5){animation-delay:.98s}.bars rect:nth-child(6){animation-delay:1.1s}
.bars rect:nth-child(7){animation-delay:1.22s}.bars rect:nth-child(8){animation-delay:1.34s}
@keyframes grow{to{transform:scaleY(1)}}
.chip{position:absolute;padding:13px 18px;border-radius:var(--rad);font-size:13px;font-weight:600;display:flex;gap:10px;align-items:center;animation:float 6s ease-in-out infinite}
.chip .ico{width:30px;height:30px;border-radius:10px;display:grid;place-items:center;background:linear-gradient(135deg,var(--a1),var(--a2));font-size:15px}
.chip.c1{top:-26px;right:-14px}.chip.c2{bottom:-24px;left:-18px;animation-delay:1.4s}
.chip small{display:block;font-size:11px;color:var(--muted);font-weight:500}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}

.logos{padding:26px 0 8px}
.logos .cap{text-align:center;font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--muted);margin-bottom:26px}
.marquee{overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent);mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)}
.marquee-track{display:flex;gap:64px;width:max-content;animation:mq 30s linear infinite;padding:6px 0}
.marquee:hover .marquee-track{animation-play-state:paused}
.marquee-track span{font-family:var(--fd);font-weight:700;font-size:19px;letter-spacing:.1em;color:var(--muted);opacity:.65;white-space:nowrap}
@keyframes mq{to{transform:translateX(-50%)}}

.sec-head{max-width:640px;margin:0 auto 60px;text-align:center}
.sec-head .kicker{font-size:12px;font-weight:700;letter-spacing:.24em;text-transform:uppercase;color:var(--a1);margin-bottom:14px}
.sec-head h2{font-size:clamp(1.7rem,3.4vw,2.6rem);font-weight:800;margin-bottom:16px}
[data-brand="nova"] .sec-head h2{font-size:clamp(1.35rem,2.8vw,2.1rem)}
.sec-head p{color:var(--muted)}

.features-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.f-card{padding:32px 28px;position:relative;overflow:hidden;transition:transform .35s,border-color .35s,box-shadow .35s}
.f-card::before{content:"";position:absolute;inset:0;background:radial-gradient(340px 200px at 15% 0%,color-mix(in srgb,var(--a1) 14%,transparent),transparent 70%);opacity:0;transition:opacity .4s}
.f-card:hover{transform:translateY(-6px);border-color:color-mix(in srgb,var(--a1) 45%,transparent);box-shadow:0 26px 70px rgba(0,0,0,.45)}
.f-card:hover::before{opacity:1}
.f-ico{width:52px;height:52px;border-radius:calc(var(--rad) - 4px);display:grid;place-items:center;margin-bottom:22px;background:linear-gradient(135deg,color-mix(in srgb,var(--a1) 26%,transparent),color-mix(in srgb,var(--a2) 18%,transparent));border:1px solid color-mix(in srgb,var(--a1) 35%,transparent)}
.f-ico svg{width:24px;height:24px;stroke:var(--a1);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.f-card h3{font-size:18.5px;margin-bottom:10px}
.f-card p{color:var(--muted);font-size:14.5px}

.steps{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.step{padding:34px 28px;position:relative}
.step .num{font-family:var(--fd);font-weight:800;font-size:52px;line-height:1;color:transparent;-webkit-text-stroke:1.5px var(--a1);opacity:.85;margin-bottom:20px}
.step h3{font-size:19px;margin-bottom:10px}
.step p{color:var(--muted);font-size:14.5px}
.step::after{content:"→";position:absolute;top:44px;right:-24px;font-size:22px;color:var(--a2);z-index:1}
.step:last-child::after{display:none}

.metrics{padding:44px 0}
.metrics-inner{display:grid;grid-template-columns:repeat(4,1fr);gap:20px;padding:44px 30px;text-align:center}
.metric .n{font-family:var(--fd);font-weight:800;font-size:clamp(1.9rem,3.6vw,2.9rem)}
.metric .n em{font-style:normal;color:var(--a2)}
.metric .t{color:var(--muted);font-size:13.5px;margin-top:6px}

.t-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.t-card{padding:30px 28px;display:flex;flex-direction:column;gap:20px;transition:transform .35s,border-color .35s}
.t-card:hover{transform:translateY(-6px);border-color:color-mix(in srgb,var(--a2) 45%,transparent)}
.t-card .quote{font-size:15px;color:var(--ink)}
.t-card .quote b{color:var(--a3)}
.t-who{display:flex;align-items:center;gap:14px;margin-top:auto}
.t-who .av{width:46px;height:46px;border-radius:50%;display:grid;place-items:center;font-weight:800;font-size:15px;color:#0b0b10;background:linear-gradient(135deg,var(--a2),var(--a3))}
.t-who .nm{font-weight:700;font-size:14.5px}
.t-who .rl{font-size:12.5px;color:var(--muted)}

.p-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;align-items:stretch}
.p-card{padding:36px 30px;display:flex;flex-direction:column;transition:transform .35s}
.p-card:hover{transform:translateY(-6px)}
.p-card .plan{font-size:13px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:14px}
.p-card .price{font-family:var(--fd);font-weight:800;font-size:38px;margin-bottom:4px}
.p-card .price small{font-size:14px;font-weight:500;color:var(--muted);font-family:var(--fb)}
.p-card .desc{color:var(--muted);font-size:13.5px;margin-bottom:24px}
.p-card ul{list-style:none;display:flex;flex-direction:column;gap:12px;margin-bottom:30px;font-size:14.5px}
.p-card li{display:flex;gap:11px;align-items:flex-start}
.p-card li svg{width:17px;height:17px;flex:none;margin-top:3px;stroke:var(--a3);fill:none;stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}
.p-card .btn{margin-top:auto;justify-content:center}
.p-card.hot{border:1px solid transparent;position:relative;background:linear-gradient(var(--bg2),var(--bg2)) padding-box,linear-gradient(140deg,var(--a1),var(--a2),var(--a3)) border-box;box-shadow:0 30px 80px color-mix(in srgb,var(--a1) 22%,transparent)}
.p-card.hot .tag{position:absolute;top:-14px;left:50%;transform:translateX(-50%);background:linear-gradient(92deg,var(--a1),var(--a2));color:#fff;font-size:11px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;padding:7px 16px;border-radius:999px}

.faq{max-width:780px;margin:0 auto;display:flex;flex-direction:column;gap:14px}
.faq-item{overflow:hidden}
.faq-item button{width:100%;display:flex;justify-content:space-between;align-items:center;gap:18px;padding:22px 26px;font-family:var(--fd);font-size:16px;font-weight:600;text-align:left}
.faq-item .plus{width:30px;height:30px;flex:none;border-radius:10px;border:1px solid var(--line);display:grid;place-items:center;transition:.35s;font-size:16px;color:var(--a1)}
.faq-item.open .plus{transform:rotate(45deg);background:color-mix(in srgb,var(--a1) 18%,transparent)}
.faq-body{max-height:0;overflow:hidden;transition:max-height .45s ease}
.faq-body p{padding:0 26px 24px;color:var(--muted);font-size:14.5px}

.cta-banner{padding:64px 48px;text-align:center;position:relative;overflow:hidden}
.cta-banner::before{content:"";position:absolute;inset:-40%;background:conic-gradient(from 0deg,transparent 0 40%,color-mix(in srgb,var(--a1) 16%,transparent) 50%,transparent 60%);animation:spin 9s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.cta-banner>*{position:relative}
.cta-banner h2{font-size:clamp(1.7rem,3.4vw,2.6rem);margin-bottom:14px}
.cta-banner p{color:var(--muted);margin-bottom:34px}
.cta-form{display:flex;gap:12px;max-width:520px;margin:0 auto}
.cta-form input{flex:1;padding:15px 22px;border-radius:var(--rad-btn);border:1px solid var(--line);background:color-mix(in srgb,var(--bg) 55%,transparent);color:var(--ink);font-family:var(--fb);font-size:15px;outline:none;transition:border-color .3s}
.cta-form input:focus{border-color:var(--a1)}
.cta-form input::placeholder{color:var(--muted)}

footer{border-top:1px solid var(--line);padding:70px 0 34px;position:relative;overflow:hidden}
.f-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:40px;margin-bottom:60px}
.f-grid h4{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted);margin-bottom:18px}
.f-grid a{display:block;color:var(--muted);font-size:14.5px;padding:5px 0;transition:color .3s,transform .3s}
.f-grid a:hover{color:var(--ink);transform:translateX(4px)}
.f-about p{color:var(--muted);font-size:14px;max-width:280px;margin-top:16px}
.f-bottom{display:flex;justify-content:space-between;gap:20px;flex-wrap:wrap;border-top:1px solid var(--line);padding-top:26px;color:var(--muted);font-size:13px}
.f-watermark{position:absolute;bottom:-6%;left:50%;transform:translateX(-50%);font-family:var(--fd);font-weight:800;font-size:clamp(120px,24vw,320px);letter-spacing:.06em;color:transparent;-webkit-text-stroke:1px color-mix(in srgb,var(--a1) 14%,transparent);pointer-events:none;user-select:none}

.brand-switch{position:fixed;left:22px;bottom:22px;z-index:70;display:flex;align-items:center;gap:12px;padding:11px 16px;border-radius:999px;background:color-mix(in srgb,var(--bg) 68%,transparent);border:1px solid var(--line);-webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);box-shadow:0 16px 44px rgba(0,0,0,.45)}
.brand-switch .bs-label{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-right:2px}
.brand-switch .bs-name{font-family:var(--fd);font-weight:700;font-size:13px;min-width:52px;color:var(--ink)}
.bs-dot{width:22px;height:22px;border-radius:50%;position:relative;transition:transform .25s}
.bs-dot:hover{transform:scale(1.18)}
.bs-dot i{position:absolute;inset:4px;border-radius:50%;background:var(--d)}
.bs-dot.active{box-shadow:0 0 0 2px var(--bg),0 0 0 4px var(--ink)}
.bs-dot[data-b="pulse"]{--d:linear-gradient(135deg,#8b5cf6,#e14fd0)}
.bs-dot[data-b="aurora"]{--d:linear-gradient(135deg,#2dd4bf,#38bdf8)}
.bs-dot[data-b="ember"]{--d:linear-gradient(135deg,#fb923c,#f43f5e)}
.bs-dot[data-b="nova"]{--d:linear-gradient(135deg,#f472b6,#a3e635)}

.reveal{opacity:0;transform:translateY(28px);transition:opacity .8s ease,transform .8s ease}
.reveal.in{opacity:1;transform:none}
.reveal[data-d="1"]{transition-delay:.1s}.reveal[data-d="2"]{transition-delay:.2s}
.reveal[data-d="3"]{transition-delay:.3s}.reveal[data-d="4"]{transition-delay:.4s}
.reveal[data-d="5"]{transition-delay:.5s}

@media(max-width:1020px){.hero-grid{grid-template-columns:1fr;gap:70px}.hero-visual{max-width:640px;margin:0 auto}.features-grid,.t-grid{grid-template-columns:repeat(2,1fr)}.steps{grid-template-columns:1fr;max-width:560px;margin:0 auto}.step::after{content:"↓";top:auto;bottom:-30px;right:50%;transform:translateX(50%)}.p-grid{grid-template-columns:1fr;max-width:480px;margin:0 auto}.metrics-inner{grid-template-columns:repeat(2,1fr);gap:36px 12px}.f-grid{grid-template-columns:1fr 1fr}}
@media(max-width:760px){.nav-links,.nav-cta .btn-ghost{display:none}.burger{display:block}section{padding:72px 0}.features-grid,.t-grid{grid-template-columns:1fr}.kpis{grid-template-columns:1fr 1fr}.kpi:nth-child(3){display:none}.chip.c1{right:0}.chip.c2{left:0}.cta-form{flex-direction:column}.cta-banner{padding:48px 24px}.brand-switch{left:12px;bottom:12px;padding:9px 12px;gap:9px}.brand-switch .bs-label{display:none}}
@media(prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important}html{scroll-behavior:auto}}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const GLASSMORPH_HTML_BODY = `
<svg width="0" height="0" style="position:absolute" aria-hidden="true">
  <defs>
    <linearGradient id="bstG" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" style="stop-color:var(--a1)"/>
      <stop offset="1" style="stop-color:var(--a2)"/>
    </linearGradient>
  </defs>
</svg>
<div class="noise"></div>
<div class="grid-bg"></div>
<div class="blob b1"></div><div class="blob b2"></div><div class="blob b3"></div>

<header id="hdr">
  <div class="container nav">
    <a class="logo" href="#top" aria-label="BST — на главную">
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <rect x="3" y="3" width="42" height="42" rx="13" fill="url(#bstG)" opacity=".16"/>
        <path d="M16 12v24M16 12h8.5a6 6 0 0 1 0 12H16M16 24h10.5a6 6 0 0 1 0 12H16" stroke="url(#bstG)" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      BST <span class="edition" id="navEdition">Pulse</span>
    </a>
    <nav class="nav-links" aria-label="Основная навигация">
      <a href="#features">Возможности</a>
      <a href="#how">Как работает</a>
      <a href="#reviews">Отзывы</a>
      <a href="#pricing">Тарифы</a>
      <a href="#faq">FAQ</a>
    </nav>
    <div class="nav-cta">
      <a href="#cta" class="btn btn-ghost">Войти</a>
      <a href="#cta" class="btn btn-primary">Начать бесплатно</a>
      <button class="burger" id="burger" aria-label="Меню"><span></span><span></span><span></span></button>
    </div>
  </div>
</header>

<div class="mnav" id="mnav">
  <a href="#features">Возможности</a>
  <a href="#how">Как работает</a>
  <a href="#reviews">Отзывы</a>
  <a href="#pricing">Тарифы</a>
  <a href="#faq">FAQ</a>
  <a href="#cta" class="grad" style="border:none">Начать бесплатно →</a>
</div>

<main id="top">
<section class="hero">
  <div class="hero-word" id="heroWord">PULSE</div>
  <div class="container hero-grid">
    <div>
      <div class="badge reveal"><span class="pulse-dot"></span><span id="heroBadge">BST Pulse · аналитика в реальном времени</span></div>
      <h1 class="reveal" data-d="1" id="heroH1">Продуктовая аналитика <span class="grad">на сверхскорости</span></h1>
      <p class="sub reveal" data-d="2" id="heroSub">BST объединяет данные, автоматизацию и ИИ-инсайты в одном стеклянном интерфейсе. Решения за секунды, рост — за недели.</p>
      <div class="hero-actions reveal" data-d="3">
        <a href="#cta" class="btn btn-primary">Попробовать бесплатно
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </a>
        <a href="#how" class="btn btn-ghost">▶&nbsp; Смотреть демо</a>
      </div>
      <div class="hero-proof reveal" data-d="4">
        <div class="avatars"><i>АК</i><i>МС</i><i>ДЛ</i><i>+9</i></div>
        <div><span class="stars">★★★★★</span> 4.9/5 — <b>12 400+</b> команд уже с BST</div>
      </div>
    </div>
    <div class="hero-visual reveal" data-d="2" id="tiltWrap">
      <div class="glass dash" id="dash">
        <div class="dash-top">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          <span class="url">app.bst.io/overview</span>
        </div>
        <div class="kpis">
          <div class="kpi"><div class="lbl">MRR</div><div class="val">₽12,4М</div><div class="delta">▲ 18,2%</div></div>
          <div class="kpi"><div class="lbl">Retention</div><div class="val">94,2%</div><div class="delta">▲ 3,1%</div></div>
          <div class="kpi"><div class="lbl">NPS</div><div class="val">72</div><div class="delta">▲ 6 пт</div></div>
        </div>
        <div class="chart">
          <svg viewBox="0 0 520 210">
            <defs>
              <linearGradient id="lineG" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" style="stop-color:var(--a1)"/><stop offset="1" style="stop-color:var(--a2)"/>
              </linearGradient>
              <linearGradient id="areaG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style="stop-color:var(--a1)" stop-opacity=".28"/><stop offset="1" style="stop-color:var(--a1)" stop-opacity="0"/>
              </linearGradient>
              <linearGradient id="barG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style="stop-color:var(--a2)" stop-opacity=".9"/><stop offset="1" style="stop-color:var(--a2)" stop-opacity=".25"/>
              </linearGradient>
            </defs>
            <g stroke="rgba(255,255,255,.07)"><line x1="0" y1="50" x2="520" y2="50"/><line x1="0" y1="100" x2="520" y2="100"/><line x1="0" y1="150" x2="520" y2="150"/></g>
            <path d="M0,160 C50,140 80,150 120,120 C160,92 200,118 240,96 C280,74 320,92 360,62 C400,38 450,58 520,26 L520,210 L0,210 Z" fill="url(#areaG)"/>
            <path class="area-line" d="M0,160 C50,140 80,150 120,120 C160,92 200,118 240,96 C280,74 320,92 360,62 C400,38 450,58 520,26"/>
            <g class="bars">
              <rect x="24"  y="140" width="26" height="60"  rx="6"/>
              <rect x="88"  y="120" width="26" height="80"  rx="6"/>
              <rect x="152" y="132" width="26" height="68"  rx="6"/>
              <rect x="216" y="100" width="26" height="100" rx="6"/>
              <rect x="280" y="112" width="26" height="88"  rx="6"/>
              <rect x="344" y="80"  width="26" height="120" rx="6"/>
              <rect x="408" y="92"  width="26" height="108" rx="6"/>
              <rect x="472" y="56"  width="26" height="144" rx="6"/>
            </g>
          </svg>
        </div>
      </div>
      <div class="chip glass c1"><span class="ico">⚡</span><span>+128% конверсия<small>за первый квартал</small></span></div>
      <div class="chip glass c2"><span class="ico">✦</span><span>ИИ-инсайт готов<small>«Удержите сегмент B2B»</small></span></div>
    </div>
  </div>
</section>

<div class="logos container">
  <div class="cap">Нам доверяют команды по всему миру</div>
  <div class="marquee"><div class="marquee-track" id="mqTrack">
    <span>NOVERA</span><span>SKYFT</span><span>LUMIO</span><span>DATKIN</span><span>ORBITA</span><span>HEXA LABS</span><span>FINTEA</span><span>CLOUDRA</span>
  </div></div>
</div>

<section id="features">
  <div class="container">
    <div class="sec-head reveal">
      <div class="kicker">Возможности</div>
      <h2 id="featH2">Всё, что нужно для роста. <span class="grad">В одном месте.</span></h2>
      <p id="featP">BST заменяет пять инструментов: аналитику, CRM, автоматизацию, отчёты и ИИ-ассистента.</p>
    </div>
    <div class="features-grid">
      <div class="f-card glass reveal"><div class="f-ico"><svg viewBox="0 0 24 24"><path d="M13 2 3 14h7l-1 8 11-13h-7l1-7z"/></svg></div><h3 data-t="feat1Title">Реал-тайм аналитика</h3><p data-t="feat1Desc">Метрики обновляются каждую секунду. Дашборды, воронки и когорты без SQL и ожидания.</p></div>
      <div class="f-card glass reveal" data-d="1"><div class="f-ico"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"/></svg></div><h3 data-t="feat2Title">ИИ-ассистент</h3><p data-t="feat2Desc">Задавайте вопросы словами — BST строит графики и объясняет аномалии сам.</p></div>
      <div class="f-card glass reveal" data-d="2"><div class="f-ico"><svg viewBox="0 0 24 24"><path d="M12 2 4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6l-8-4z"/><path d="m9 12 2 2 4-4"/></svg></div><h3 data-t="feat3Title">Безопасность уровня банка</h3><p data-t="feat3Desc">Шифрование end-to-end, SOC 2 и ISO 27001. Данные остаются только вашими.</p></div>
      <div class="f-card glass reveal"><div class="f-ico"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg></div><h3 data-t="feat4Title">150+ интеграций</h3><p data-t="feat4Desc">Slack, Notion, Telegram, 1С, amoCRM — подключение в два клика, без разработчиков.</p></div>
      <div class="f-card glass reveal" data-d="1"><div class="f-ico"><svg viewBox="0 0 24 24"><path d="M3 3v18h18"/><path d="m7 14 4-4 3 3 5-6"/></svg></div><h3 data-t="feat5Title">Автоотчёты</h3><p data-t="feat5Desc">Еженедельные дайджесты прилетают в Telegram сами. Красиво и по делу.</p></div>
      <div class="f-card glass reveal" data-d="2"><div class="f-ico"><svg viewBox="0 0 24 24"><circle cx="9" cy="7" r="3"/><circle cx="17" cy="9" r="3"/><path d="M3 21v-1a6 6 0 0 1 12 0v1M15 21v-.5a5 5 0 0 1 6-4.9"/></svg></div><h3 data-t="feat6Title">Совместная работа</h3><p data-t="feat6Desc">Комментарии, роли и общие пространства — вся команда в едином контексте.</p></div>
    </div>
  </div>
</section>

<section id="how">
  <div class="container">
    <div class="sec-head reveal">
      <div class="kicker">Как это работает</div>
      <h2 id="howH2">Запуск за <span class="grad">15 минут</span>, а не за квартал</h2>
    </div>
    <div class="steps">
      <div class="step glass reveal"><div class="num">01</div><h3 data-t="how1Title">Подключите данные</h3><p data-t="how1Desc">Один сниппет кода или готовая интеграция — и BST уже собирает события.</p></div>
      <div class="step glass reveal" data-d="1"><div class="num">02</div><h3 data-t="how2Title">Настройте дашборд</h3><p data-t="how2Desc">Перетащите виджеты, ИИ предложит метрики под вашу бизнес-модель.</p></div>
      <div class="step glass reveal" data-d="2"><div class="num">03</div><h3 data-t="how3Title">Растите на инсайтах</h3><p data-t="how3Desc">Автоматизации и алерты работают 24/7 — вы принимаете решения.</p></div>
    </div>
  </div>
</section>

<section class="metrics">
  <div class="container">
    <div class="glass metrics-inner reveal">
      <div class="metric"><div class="n"><span data-count="12400">0</span><em>+</em></div><div class="t">команд используют BST</div></div>
      <div class="metric"><div class="n"><span data-count="99.98" data-dec="2">0</span><em>%</em></div><div class="t">аптайм за 3 года</div></div>
      <div class="metric"><div class="n"><span data-count="3.2" data-dec="1">0</span><em>×</em></div><div class="t">средний рост ROI</div></div>
      <div class="metric"><div class="n"><span data-count="150">0</span><em>+</em></div><div class="t">готовых интеграций</div></div>
    </div>
  </div>
</section>

<section id="reviews">
  <div class="container">
    <div class="sec-head reveal">
      <div class="kicker">Отзывы</div>
      <h2 id="reviewsH2">Команды говорят <span class="grad">за нас</span></h2>
    </div>
    <div class="t-grid">
      <div class="t-card glass reveal"><p class="quote" data-t="rev1Quote">«Перешли с трёх разных инструментов на BST. Отчёты, которые раньше занимали день, теперь — <b>секунды</b>. Команда в восторге.»</p><div class="t-who"><span class="av">АК</span><div><div class="nm" data-t="rev1Name">Анна Ковалёва</div><div class="rl" data-t="rev1Role">CPO, NOVERA</div></div></div></div>
      <div class="t-card glass reveal" data-d="1"><p class="quote" data-t="rev2Quote">«ИИ-инсайты нашли утечку в воронке, которую мы не видели год. BST окупился <b>за первую неделю</b>.»</p><div class="t-who"><span class="av">МС</span><div><div class="nm" data-t="rev2Name">Максим Соколов</div><div class="rl" data-t="rev2Role">CEO, SKYFT</div></div></div></div>
      <div class="t-card glass reveal" data-d="2"><p class="quote" data-t="rev3Quote">«Наконец-то SaaS, который выглядит так же хорошо, как работает. Интерфейс — <b>чистое стекло</b>, всё на своих местах.»</p><div class="t-who"><span class="av">ДЛ</span><div><div class="nm" data-t="rev3Name">Дарья Лебедева</div><div class="rl" data-t="rev3Role">Head of Design, LUMIO</div></div></div></div>
    </div>
  </div>
</section>

<section id="pricing">
  <div class="container">
    <div class="sec-head reveal">
      <div class="kicker">Тарифы</div>
      <h2 id="priceH2">Честные цены. <span class="grad">Без сюрпризов.</span></h2>
      <p id="priceP">14 дней бесплатно на любом тарифе. Карта не нужна.</p>
    </div>
    <div class="p-grid">
      <div class="p-card glass reveal">
        <div class="plan" data-t="plan1Name">Старт</div><div class="price" data-t="plan1Price">0 ₽</div>
        <div class="desc" data-t="plan1Desc">Для соло-фаундеров и небольших проектов</div>
        <ul>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>До 3 пользователей</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>Базовые дашборды</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>10 интеграций</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>История 30 дней</li>
        </ul>
        <a href="#cta" class="btn btn-ghost">Начать бесплатно</a>
      </div>
      <div class="p-card glass hot reveal" data-d="1">
        <div class="tag">Выбор команд</div>
        <div class="plan" data-t="plan2Name">Команда</div><div class="price" data-t="plan2Price">4 900 ₽<small> /мес</small></div>
        <div class="desc" data-t="plan2Desc">Для растущих продуктовых команд</div>
        <ul>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>До 25 пользователей</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>ИИ-ассистент и инсайты</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>150+ интеграций</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>Автоотчёты в Telegram</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>Приоритетная поддержка</li>
        </ul>
        <a href="#cta" class="btn btn-primary">Выбрать Команду</a>
      </div>
      <div class="p-card glass reveal" data-d="2">
        <div class="plan" data-t="plan3Name">Бизнес</div><div class="price" data-t="plan3Price">По запросу</div>
        <div class="desc" data-t="plan3Desc">Для компаний с высокими требованиями</div>
        <ul>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>Безлимит пользователей</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>SSO и аудит-логи</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>Выделенный менеджер</li>
          <li><svg viewBox="0 0 24 24"><path d="m4 12 5 5L20 7"/></svg>SLA 99,99%</li>
        </ul>
        <a href="#cta" class="btn btn-ghost">Связаться</a>
      </div>
    </div>
  </div>
</section>

<section id="faq">
  <div class="container">
    <div class="sec-head reveal">
      <div class="kicker">FAQ</div>
      <h2 id="faqH2">Вопросы и <span class="grad">ответы</span></h2>
    </div>
    <div class="faq reveal">
      <div class="faq-item glass"><button>Нужны ли разработчики для запуска?<span class="plus">+</span></button><div class="faq-body"><p>Нет. Готовые интеграции подключаются в два клика. Свой код понадобится только для редких кастомных событий — сниппет занимает 4 строки.</p></div></div>
      <div class="faq-item glass"><button>Где хранятся данные?<span class="plus">+</span></button><div class="faq-body"><p>В сертифицированных дата-центрах (SOC 2, ISO 27001). Шифрование при передаче и хранении, доступ — только у вашей команды.</p></div></div>
      <div class="faq-item glass"><button>Можно ли перейти с другого инструмента?<span class="plus">+</span></button><div class="faq-body"><p>Да, импортер перенесёт историю из популярных платформ автоматически, обычно за одну ночь. На тарифе «Бизнес» миграцию ведёт наш инженер.</p></div></div>
      <div class="faq-item glass"><button>Что будет после пробного периода?<span class="plus">+</span></button><div class="faq-body"><p>Ничего страшного: аккаунт переключится на бесплатный «Старт», все данные сохранятся. Апгрейд — в любой момент.</p></div></div>
      <div class="faq-item glass"><button>Есть ли скидки стартапам?<span class="plus">+</span></button><div class="faq-body"><p>Да, командам младше 2 лет с инвестициями до $1М мы даём 50% скидку на первый год. Напишите в чат — оформим за день.</p></div></div>
    </div>
  </div>
</section>

<section id="cta">
  <div class="container">
    <div class="glass cta-banner reveal">
      <h2 id="ctaH2">Готовы увидеть свой рост <span class="grad">в новом свете?</span></h2>
      <p id="ctaP">Присоединяйтесь к 12 400+ командам. Настройка — 15 минут.</p>
      <form class="cta-form" id="ctaForm">
        <input type="email" required placeholder="рабочий@email.com" aria-label="Email">
        <button class="btn btn-primary" type="submit">Создать аккаунт</button>
      </form>
    </div>
  </div>
</section>
</main>

<footer>
  <div class="container">
    <div class="f-grid">
      <div class="f-about">
        <a class="logo" href="#top">
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <rect x="3" y="3" width="42" height="42" rx="13" fill="url(#bstG)" opacity=".16"/>
            <path d="M16 12v24M16 12h8.5a6 6 0 0 1 0 12H16M16 24h10.5a6 6 0 0 1 0 12H16" stroke="url(#bstG)" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          BST
        </a>
        <p id="footerDesc">Платформа продуктовой аналитики и автоматизации нового поколения. Сделано с любовью к деталям.</p>
      </div>
      <div><h4>Продукт</h4><a href="#features">Возможности</a><a href="#pricing">Тарифы</a><a href="#how">Интеграции</a><a href="#cta">Changelog</a></div>
      <div><h4>Компания</h4><a href="#top">О нас</a><a href="#reviews">Клиенты</a><a href="#top">Карьера</a><a href="#top">Блог</a></div>
      <div><h4>Поддержка</h4><a href="#faq">FAQ</a><a href="#top">Документация</a><a href="#top">Статус</a><a href="#top">Контакты</a></div>
    </div>
    <div class="f-bottom">
      <span>© 2025 BST. Все права защищены.</span>
      <span>Политика конфиденциальности · Условия использования</span>
    </div>
  </div>
  <div class="f-watermark" id="fWatermark">BST</div>
</footer>

<div class="brand-switch" role="group" aria-label="Вариант бренда (клавиши 1–4)">
  <span class="bs-label">Бренд</span>
  <span class="bs-name" id="bsName">Pulse</span>
  <button class="bs-dot active" data-b="pulse"  title="Pulse — фиолетовый неон" aria-label="Бренд Pulse"><i></i></button>
  <button class="bs-dot" data-b="aurora" title="Aurora — полярная бирюза" aria-label="Бренд Aurora"><i></i></button>
  <button class="bs-dot" data-b="ember"  title="Ember — тёплый закат" aria-label="Бренд Ember"><i></i></button>
  <button class="bs-dot" data-b="nova"   title="Nova — роза и лайм" aria-label="Бренд Nova"><i></i></button>
</div>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function glassmorphEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
const COPY = {
  pulse:{name:'Pulse',bg:'#0a0812',badge:'BST Pulse · аналитика в реальном времени',h1:'Продуктовая аналитика <span class="grad">на сверхскорости</span>',sub:'BST объединяет данные, автоматизацию и ИИ-инсайты в одном стеклянном интерфейсе. Решения за секунды, рост — за недели.'},
  aurora:{name:'Aurora',bg:'#03141a',badge:'BST Aurora · ясность северного сияния',h1:'Ясность данных в каждом <span class="grad">вашем решении</span>',sub:'Мягкий свет полярного сияния вместо шума таблиц: BST превращает хаос метрик в спокойную, прозрачную картину бизнеса.'},
  ember:{name:'Ember',bg:'#120905',badge:'BST Ember · энергия вашего роста',h1:'Заряжайте команду <span class="grad">энергией роста</span>',sub:'Тёплый, дерзкий и быстрый: BST разжигает продажи — автоматизация рутины, фокус на результате, пламя в глазах команды.'},
  nova:{name:'Nova',bg:'#0d0614',badge:'BST Nova · платформа из будущего',h1:'Будущее вашей команды <span class="grad">уже здесь</span>',sub:'Неоновая смелость и ИИ-магия: BST — инструмент для тех, кто не догоняет рынок, а создаёт его.'}
};
const root = document.documentElement;
const $ = s => document.querySelector(s);

function applyBrand(b){
  const c = COPY[b]; if(!c) return;
  root.setAttribute('data-brand', b);
  const metaTheme = $('#metaTheme'); if(metaTheme) metaTheme.setAttribute('content', c.bg);
  const navEd = $('#navEdition'); if(navEd) navEd.textContent = c.name;
  const hBadge = $('#heroBadge'); if(hBadge) hBadge.textContent = c.badge;
  const hH1 = $('#heroH1'); if(hH1) hH1.innerHTML = c.h1;
  const hSub = $('#heroSub'); if(hSub) hSub.textContent = c.sub;
  const hWord = $('#heroWord'); if(hWord) hWord.textContent = c.name.toUpperCase();
  const fWm = $('#fWatermark'); if(fWm) fWm.textContent = c.name.toUpperCase();
  const bsName = $('#bsName'); if(bsName) bsName.textContent = c.name;
  document.querySelectorAll('.bs-dot').forEach(d => d.classList.toggle('active', d.dataset.b === b));
  try{ localStorage.setItem('bst-brand', b); }catch(e){}
}
document.querySelectorAll('.bs-dot').forEach(d => d.addEventListener('click', () => applyBrand(d.dataset.b)));
addEventListener('keydown', e => {
  const map = {'1':'pulse','2':'aurora','3':'ember','4':'nova'};
  if(map[e.key] && !/INPUT|TEXTAREA/.test(document.activeElement.tagName)) applyBrand(map[e.key]);
});
let saved = null; try{ saved = localStorage.getItem('bst-brand'); }catch(e){}
applyBrand(saved && COPY[saved] ? saved : '${brand}');

const hdr = $('#hdr');
if(hdr) addEventListener('scroll', () => hdr.classList.toggle('scrolled', scrollY > 30), {passive:true});

${mode === 'canvas' ? `document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
document.querySelectorAll('[data-count]').forEach(el => {
  const t = parseFloat(el.dataset.count), d = +(el.dataset.dec||0);
  el.textContent = d ? t.toFixed(d).replace('.',',') : Math.round(t).toLocaleString('ru-RU');
});` : `const io = new IntersectionObserver(es => es.forEach(e => {
  if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); }
}), {threshold:.14});
document.querySelectorAll('.reveal').forEach(el => io.observe(el));
const cio = new IntersectionObserver(es => es.forEach(e => {
  if(!e.isIntersecting) return; cio.unobserve(e.target);
  const el = e.target, target = parseFloat(el.dataset.count), dec = +(el.dataset.dec||0), t0 = performance.now();
  (function tick(t){
    const p = Math.min((t-t0)/1600,1), ease = 1-Math.pow(1-p,4), v = target*ease;
    el.textContent = dec ? v.toFixed(dec).replace('.',',') : Math.round(v).toLocaleString('ru-RU');
    if(p<1) requestAnimationFrame(tick);
  })(t0);
}), {threshold:.5});
document.querySelectorAll('[data-count]').forEach(el => cio.observe(el));`}

document.querySelectorAll('.faq-item').forEach(item => {
  const btn = item.querySelector('button'), body = item.querySelector('.faq-body');
  if(!btn||!body) return;
  btn.addEventListener('click', () => {
    const open = item.classList.contains('open');
    document.querySelectorAll('.faq-item.open').forEach(o => {
      o.classList.remove('open'); const b = o.querySelector('.faq-body'); if(b) b.style.maxHeight = null;
    });
    if(!open){ item.classList.add('open'); body.style.maxHeight = body.scrollHeight + 'px'; }
  });
});

const burger = $('#burger');
if(burger) burger.addEventListener('click', () => document.body.classList.toggle('menu-open'));
document.querySelectorAll('.mnav a').forEach(a => a.addEventListener('click', () => document.body.classList.remove('menu-open')));

const ctaForm = $('#ctaForm');
if(ctaForm) ctaForm.addEventListener('submit', e => {
  e.preventDefault();
  const btn = e.target.querySelector('button');
  if(btn){ btn.textContent = '✓ Готово! Проверьте почту'; btn.style.pointerEvents = 'none'; }
  const inp = e.target.querySelector('input');
  if(inp) inp.value = '';
  setTimeout(() => { if(btn){ btn.textContent = 'Создать аккаунт'; btn.style.pointerEvents = ''; } }, 3200);
});

const mq = $('#mqTrack'); if(mq) mq.innerHTML += mq.innerHTML;

if(matchMedia('(pointer:fine)').matches){
  const wrap = $('#tiltWrap'), dash = $('#dash');
  if(wrap && dash){
    wrap.addEventListener('mousemove', e => {
      const r = wrap.getBoundingClientRect();
      const x = (e.clientX - r.left)/r.width - .5, y = (e.clientY - r.top)/r.height - .5;
      dash.style.transform = \`rotateY(\${x*7}deg) rotateX(\${-y*7}deg)\`;
    });
    wrap.addEventListener('mouseleave', () => dash.style.transform = 'rotateY(0) rotateX(0)');
  }
}
${mode === 'canvas' ? `
(function(){
  document.addEventListener('click', function(e){
    var a = e.target.closest('a[href]');
    if(a){ e.preventDefault(); }
  }, true);
  document.querySelectorAll('form').forEach(function(f){
    f.addEventListener('submit', function(e){ e.preventDefault(); });
  });
})();
` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getGlassmorphPageHtml(data: GlassmorphPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'pulse'
  return `<!DOCTYPE html>
<html lang="ru" data-brand="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="theme-color" id="metaTheme" content="#0a0812">
<title>BST — платформа нового поколения</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource-variable/inter@5/index.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource-variable/space-grotesk@5/index.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource-variable/sora@5/index.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource-variable/outfit@5/index.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource-variable/unbounded@5/index.css">
<style>${GLASSMORPH_CSS}</style>
${mode === 'canvas' ? `<style>
/* Canvas overrides: iframe height=5000px makes 100vh/svh=5000px, pushing content out of view */
.hero{min-height:0!important}
section{padding:64px 0}
/* Guarantee reveal elements are visible without waiting for IntersectionObserver */
.reveal{opacity:1!important;transform:none!important}
/* Fixed blobs look wrong at arbitrary scroll; switch to absolute so they anchor to page top */
.blob,.noise,.grid-bg{position:absolute}
header{position:sticky;top:0}
</style>` : ''}
</head>
<body>
${GLASSMORPH_HTML_BODY}
<script>${glassmorphEngineJs(brand, mode)}</script>
</body>
</html>`
}
